import { type AllergyIntoleranceId, asAllergyIntoleranceId } from "@repo/domain";
import { z } from "zod";
import { newEntityIdSchema } from "./entity-id.schemas";
import { patientRefSchema } from "./patient.schemas";
import { practitionerRefSchema } from "./practitioner.schemas";
import { codeableConceptSchema } from "./schedule.schemas";

const clinicalStatusSchema = z.enum(["active", "inactive", "resolved"]);
const verificationStatusSchema = z.enum(["unconfirmed", "confirmed", "refuted", "entered-in-error"]);
const typeSchema = z.enum(["allergy", "intolerance"]);
const categorySchema = z.array(z.enum(["food", "medication", "environment", "biologic"]));
const criticalitySchema = z.enum(["low", "high", "unable-to-assess"]);
const reactionSchema = z.array(
  z.object({
    manifestation: z.array(codeableConceptSchema),
    severity: z.enum(["mild", "moderate", "severe"]).optional(),
    description: z.string().optional(),
  }),
);

export const allergyIntoleranceIdSchema: z.ZodType<AllergyIntoleranceId, z.ZodTypeDef, string> = z
  .string()
  .min(1)
  .transform(asAllergyIntoleranceId);

export const recordAllergyInputSchema = z.object({
  id: newEntityIdSchema,
  patient: patientRefSchema,
  code: codeableConceptSchema,
  clinicalStatus: clinicalStatusSchema.default("active"),
  verificationStatus: verificationStatusSchema.default("unconfirmed"),
  type: typeSchema.optional(),
  category: categorySchema.optional(),
  criticality: criticalitySchema.optional(),
  recordedDate: z.string().optional(),
  recorder: practitionerRefSchema.optional(),
  reaction: reactionSchema.optional(),
});
export type RecordAllergyInput = z.infer<typeof recordAllergyInputSchema>;

export const allergyIdInputSchema = z.object({ allergyIntoleranceId: allergyIntoleranceIdSchema });
export type AllergyIdInput = z.infer<typeof allergyIdInputSchema>;

export const updateAllergyInputSchema = z.object({
  allergyIntoleranceId: allergyIntoleranceIdSchema,
  clinicalStatus: clinicalStatusSchema.optional(),
  verificationStatus: verificationStatusSchema.optional(),
  type: typeSchema.optional(),
  category: categorySchema.optional(),
  criticality: criticalitySchema.optional(),
  reaction: reactionSchema.optional(),
});
export type UpdateAllergyInput = z.infer<typeof updateAllergyInputSchema>;
